/**
 * The legal and contact facts about the business behind WeShip, in one place,
 * because they appear in a dozen others: the site footer, the policy pages,
 * the landing page and anywhere the trading name is shown next to the
 * registered one.
 *
 * THIS IS A MIRROR of services/api/internal/company/company.go, which the
 * emails read from. The portal is a static bundle and cannot import Go, so the
 * two files are kept in step by hand. Change one, change the other — and
 * apps/customer/src/lib/company.ts, which says the same for the mobile app.
 */

/**
 * Marks a fact nobody has supplied yet. Everything that renders company
 * details skips an unset field rather than printing a placeholder: a footer
 * missing a VAT number is an omission, while a footer showing TODO or a
 * made-up number is a false statement about a registered company.
 */
export const UNSET = "TODO";

interface Company {
  tradingName: string;
  legalName: string;
  registrationNumber: string;
  vatNumber: string;
  supportEmail: string;
  privacyEmail: string;
  billingEmail: string;
  phone: string;
  streetAddress: string;
  operatingHours: string;
}

// Deliberately not `as const`: with literal types, comparing an unsupplied
// field to a supplied one is a type error rather than the runtime check it
// needs to be.
export const company: Company = {
  /** What customers see, and what the brand is. */
  tradingName: "WeShip",

  /**
   * The registered entity that trades as WeShip. WeShip and Delicate Courier
   * are the same company; contracts, invoices and the payment gateway are all
   * in the registered entity's name.
   */
  legalName: "Delicate Courier (Pty) Ltd",

  /** CIPC company registration number. */
  registrationNumber: "2025/056408/07",

  /**
   * SARS VAT registration number. This business is NOT VAT registered, so it
   * stays unset and no VAT line appears anywhere. See
   * services/api/internal/pricing, where the rate is zero for the same reason.
   */
  vatNumber: UNSET,

  supportEmail: "support@weship.co.za",
  privacyEmail: "privacy@weship.co.za",
  billingEmail: "billing@weship.co.za",

  /** The operations line customers should reach. TODO. */
  phone: UNSET,
  /** The registered physical address, one line. TODO. */
  streetAddress: UNSET,
  /** When collections actually happen. TODO. */
  operatingHours: UNSET,
};

/**
 * Where this business actually collects and delivers. An overstated service
 * area is worse than a narrow one: somebody told "nationwide" who then cannot
 * book has been misled. Widen it when the vehicles do.
 */
export const SERVICE_AREA = "Gauteng";

/** Whether a fact has been supplied. Call this before rendering a field. */
export function isSet(value: string): boolean {
  return value !== "" && value !== UNSET;
}

/**
 * Names the business the way a legal document should: the registered entity,
 * and the name customers know it by, when the two differ and both are known.
 * Degrades to whichever is available rather than inventing the other.
 */
export function describeCompany(): string {
  const { legalName, tradingName } = company;
  if (!isSet(legalName) || legalName === tradingName) return tradingName;
  return `${legalName} t/a ${tradingName}`;
}

/** The identification line for a footer: whichever facts are known, no more. */
export function identityLine(): string {
  const parts = [describeCompany()];
  if (isSet(company.registrationNumber)) parts.push(`Reg. ${company.registrationNumber}`);
  if (isSet(company.vatNumber)) parts.push(`VAT ${company.vatNumber}`);
  return parts.join(" · ");
}
